import { Immutable } from "immer";
import React from "react";

import { StyleguideBlockRenderer } from "./StyleguideBlockRenderer";

type ILooseComponentsMap = React.ComponentProps<
    typeof StyleguideBlockRenderer
>["components"];

/**
 * Structural equivalent of each site's codegen'd
 * ``templates.StyleguideBlockTemplate`` props (this module cannot import a
 * site's ``@reactivated`` output).
 */
type IStyleguideTemplateProps = Immutable<{
    block: ReadonlyArray<{ id: string; type: string; value: unknown }>;
    map_key: string;
    bg_color: string | null;
}>;

/**
 * Build a site's ``StyleguideBlockTemplate`` component from its registry of
 * production StreamField component maps (keyed by the ``map_key`` values used
 * in the site's ``BLOCK_RENDER_ROUTING`` table).
 *
 * The styleguide renderer dispatches on a runtime block-type string, so the
 * per-block value types cannot be proven statically: each component in a map
 * is only ever invoked with the serialized value of its own block type. This
 * factory is the single place that type-erasure happens; registry entries are
 * still checked to be maps of React components.
 */
export const makeStyleguideTemplate = <
    R extends Record<string, Record<string, React.ComponentType<never>>>,
>(
    registry: R,
    opts?: {
        /** Map keys whose production page assembly renders blocks with a
         * bare `{% include_block block %}` loop (no Wagtail
         * `w-block-{type} block-{type}` wrapper div) — e.g. the PDP module
         * assemblies. Fixtures routed through these maps render bare too,
         * keeping the styleguide pixel-faithful to the page the map
         * mirrors. */
        bareMapKeys?: ReadonlyArray<Extract<keyof R, string>>;
    },
): ((props: IStyleguideTemplateProps) => React.JSX.Element) => {
    const isRegistryKey = (key: string): key is Extract<keyof R, string> =>
        key in registry;
    const Template = (props: IStyleguideTemplateProps): React.JSX.Element => {
        if (!isRegistryKey(props.map_key)) {
            throw new Error(
                `No styleguide component map registered for map_key ` +
                    `"${props.map_key}" (known: ${Object.keys(registry).join(", ")})`,
            );
        }
        const components = registry[
            props.map_key
        ] as unknown as ILooseComponentsMap;
        return (
            <StyleguideBlockRenderer
                value={props.block}
                components={components}
                bgColor={props.bg_color}
                bare={opts?.bareMapKeys?.includes(props.map_key) ?? false}
            />
        );
    };
    return Template;
};
